import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { downloadSession } from './downloadSession';

export interface IBatchEntry {
	id: string;
	label: string;
	kind: 'Role-play' | 'Real call';
	markdown: string;
	savedAt: number;
	downloaded: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

export const batchFileName = (count: number, d = new Date()) =>
	`coach-lab-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}-${count}-${count === 1 ? 'call' : 'calls'}.md`;

/** Every saved session in one file, each under its own "# Coach Lab session" heading, which is how the editing loop reads them. */
export const batchMarkdown = (entries: Pick<IBatchEntry, 'markdown'>[]) =>
	entries.map(e => e.markdown.trim()).join('\n\n---\n\n') + '\n';

const read = (key: string): IBatchEntry[] => {
	try {
		const raw = window.sessionStorage.getItem(key);
		return raw ? (JSON.parse(raw) as IBatchEntry[]) : [];
	} catch {
		return [];
	}
};

/**
 * The calls saved during this sitting, downloaded together as one file. Kept in the tab's sessionStorage so a refresh
 * mid-sitting loses nothing, and gone when the tab closes; saving sessions to the server replaces this.
 */
export const useSessionBatch = () => {
	const userId = useUserSession()?.user?.id;
	const key = `coachLab:${userId ?? 'signed-out'}:batch`;
	const [entries, setEntries] = React.useState<IBatchEntry[]>(() => (userId ? read(key) : []));

	React.useEffect(() => {
		if (!userId) return;
		try {
			window.sessionStorage.setItem(key, JSON.stringify(entries));
		} catch {
			// Storage full or blocked: the list still works until the tab is closed or reloaded.
		}
	}, [entries, key, userId]);

	const add = (entry: Pick<IBatchEntry, 'label' | 'kind' | 'markdown'>) =>
		setEntries(prev => [
			...prev,
			{ ...entry, downloaded: false, id: `${Date.now()}-${prev.length}`, savedAt: Date.now() },
		]);

	const remove = (id: string) => setEntries(prev => prev.filter(e => e.id !== id));

	const downloadAll = () => {
		if (entries.length === 0) return;
		downloadSession(batchFileName(entries.length), batchMarkdown(entries));
		setEntries(prev => prev.map(e => ({ ...e, downloaded: true })));
	};

	const clear = () => setEntries([]);

	const notDownloaded = entries.filter(e => !e.downloaded).length;

	return { add, clear, downloadAll, entries, notDownloaded, remove };
};

export type SessionBatch = ReturnType<typeof useSessionBatch>;
